import { DateTime } from "luxon";

import { formatPercentage } from "~/components/domain/reporting/ag-grid/utils/grid-helpers";
import type { BoundFormatMoney } from "~/modules/multi-currency/ui/format-money";
import { formatMoney } from "~/modules/multi-currency/ui/format-money";
import { instantToOrgCalendarDayString } from "~/utils/datetime";
import type { MoneyKind } from "~/utils/types/assistant-report-artifact";
import { formatCalendarDay, isMonthBucketValue } from "~/utils/types/assistant-report-artifact";
import { Decimal } from "~/utils/types/decimal";

/** Sub-cent prices are real: a resin quoted at $0.4235/lb is not $0.42. */
const MAX_MONEY_DECIMALS = 4;
const CENT_TOLERANCE = 1e-6;

/**
 * How many decimals a money value should show.
 *
 * A field that declares its `moneyKind` decides for itself. Undeclared fields keep the familiar
 * two decimals whenever the amount is exact in cents, and only widen when the amount genuinely
 * carries sub-cent precision, so document totals never grow trailing zeros.
 */
export const assistantMoneyDecimals = (value: number, moneyKind?: MoneyKind): number => {
  if (moneyKind === "unitPrice") {
    return MAX_MONEY_DECIMALS;
  }
  if (moneyKind === "total") {
    return 2;
  }
  const cents = value * 100;
  return Math.abs(cents - Math.round(cents)) < CENT_TOLERANCE ? 2 : MAX_MONEY_DECIMALS;
};

/**
 * Currency units to cents, keeping the sub-cent digits we are willing to show and dropping the
 * binary-floating-point dust below them (12.34 must reach the formatter as 1234, not 1233.9999…).
 */
const moneyCents = (value: number): number => Math.round(value * 10 ** (2 + MAX_MONEY_DECIMALS)) / 10 ** MAX_MONEY_DECIMALS;

/** Format an amount held in currency units (not cents) in the org's own currency. */
export const formatAssistantDollarAmount = (value: unknown, formatOrgMoney: BoundFormatMoney, moneyKind?: MoneyKind): string => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "";
  }
  return formatOrgMoney(moneyCents(value), { precision: assistantMoneyDecimals(value, moneyKind), minimumPrecision: 2 });
};

/** Format an amount held in currency units against the currency the row itself is denominated in. */
export const formatAssistantRowMoney = (value: unknown, currencyCode: unknown, moneyKind?: MoneyKind): string => {
  if (typeof value !== "number" || !Number.isFinite(value) || typeof currencyCode !== "string") {
    return "";
  }
  return formatMoney(moneyCents(value), currencyCode, { precision: assistantMoneyDecimals(value, moneyKind), minimumPrecision: 2 });
};

export const formatAssistantPercentage = (value: unknown): string =>
  typeof value === "number" && Number.isFinite(value) ? formatPercentage(value, 2) : "";

export const formatAssistantNumber = (value: unknown): string => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "";
  }
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export const formatAssistantDecimalString = (value: unknown): string => {
  if (typeof value !== "string") {
    return "";
  }
  try {
    const [integerPart, fractionalPart = ""] = new Decimal(value).toFixed(4).split(".");
    const displayFraction = fractionalPart.replace(/0+$/, "").padEnd(2, "0");
    return `${integerPart}.${displayFraction}`;
  } catch {
    return "";
  }
};

export const compareAssistantDecimalStrings = (left: unknown, right: unknown): number => {
  if (typeof left !== "string") {
    return typeof right === "string" ? -1 : 0;
  }
  if (typeof right !== "string") {
    return 1;
  }
  try {
    return new Decimal(left).comparedTo(new Decimal(right));
  } catch {
    return left.localeCompare(right);
  }
};

export const exactAssistantDecimalString = (value: unknown): string => (typeof value === "string" ? value : "");

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export { formatCalendarDay };

export const formatMonthBucket = (value: unknown): string => {
  if (typeof value !== "string" || !isMonthBucketValue(value)) {
    return "";
  }
  const [year, month] = value.split("-");
  const monthName = month ? monthNames[Number(month) - 1] : undefined;
  return year && monthName ? `${monthName} ${year}` : "";
};

// An instant cell was validated once as an ISO date-time Date.parse reads; every reader below
// starts from that one epoch and never re-parses the text with a second parser.
const instantDate = (value: unknown): Date | null => {
  if (typeof value !== "string") {
    return null;
  }
  const epoch = Date.parse(value);
  return Number.isFinite(epoch) ? new Date(epoch) : null;
};

/** "2026-10-20T02:30:00.000Z" in America/New_York -> "Oct 19, 2026, 10:30 PM". */
export const formatInstant = (value: unknown, timeZone: string): string => {
  const date = instantDate(value);
  if (!date) {
    return "";
  }
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone,
  }).format(date);
};

/** Export text for an instant: the org wall time as "2026-10-19 22:30". */
export const instantExportText = (value: unknown, timeZone: string): string => {
  const date = instantDate(value);
  return date ? DateTime.fromJSDate(date).setZone(timeZone).toFormat("yyyy-MM-dd HH:mm") : "";
};

/** The org calendar day (YYYY-MM-DD) an instant falls on. */
export const instantOrgDay = (value: unknown, timeZone: string): string => {
  const date = instantDate(value);
  return date ? instantToOrgCalendarDayString(date, timeZone) : "";
};

/**
 * Export header for an instant column, naming the zone its wall times are in: "Created (ET)".
 * The generic zone name holds on both sides of a daylight-saving change, so an October moment
 * exported in December is not labeled with December's offset.
 */
export const instantExportHeader = (label: string, timeZone: string): string => {
  const zoneName = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "shortGeneric" })
    .formatToParts(new Date())
    .find(part => part.type === "timeZoneName")?.value;
  return zoneName ? `${label} (${zoneName})` : label;
};
